
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PEOQuote } from '@/types/quotes.types';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';

export interface EditForm {
  monthlyAmount: string;
  annualAmount: string;
  validUntil: string;
  quoteFile: File | null;
}

export const useQuoteEdit = (onSuccess: () => void) => {
  const { toast } = useToast();
  const [selectedQuote, setSelectedQuote] = useState<PEOQuote | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    monthlyAmount: '',
    annualAmount: '',
    validUntil: '',
    quoteFile: null
  });

  const openEditDialog = (quote: PEOQuote) => {
    console.log('Opening edit dialog with quote:', quote);
    setSelectedQuote(quote);
    setEditForm({
      monthlyAmount: quote.monthly_cost !== null ? quote.monthly_cost.toString() : '',
      annualAmount: quote.annual_cost !== null ? quote.annual_cost.toString() : '',
      validUntil: quote.valid_until || '',
      quoteFile: null
    });
    setUploadError(null);
    setUpdateError(null);
    setIsEditDialogOpen(true);
  };

  const handleEditQuote = async () => {
    if (!selectedQuote) {
      console.error('No quote selected for editing');
      return;
    }
    
    console.log('Starting quote update with form data:', editForm);
    setIsSubmitting(true);
    setUploadError(null);
    setUpdateError(null);
    
    try {
      let quoteFileUrl = selectedQuote.quote_file_url;

      if (editForm.quoteFile) {
        try {
          const fileExt = editForm.quoteFile.name.split('.').pop();
          const fileName = `${selectedQuote.application_id}/${selectedQuote.provider_id}/${Date.now()}.${fileExt}`;
          
          // Debug logs to trace file upload
          console.log('Starting file upload:', {
            fileName,
            fileSize: editForm.quoteFile.size,
            fileType: editForm.quoteFile.type
          });
          
          // Check if storage bucket exists and create it if not
          const { data: buckets } = await supabase.storage.listBuckets();
          const bucketExists = buckets?.some(bucket => bucket.name === 'peo-quotes');
          
          if (!bucketExists) {
            console.log('Creating peo-quotes bucket');
            const { error: createError } = await supabase.storage.createBucket('peo-quotes', {
              public: true
            });
            
            if (createError) {
              console.error('Error creating bucket:', createError);
              setUploadError('Failed to create storage bucket: ' + createError.message);
              throw createError;
            }
          }
          
          const { data, error: uploadError } = await supabase.storage
            .from('peo-quotes')
            .upload(fileName, editForm.quoteFile, { upsert: true });
          
          if (uploadError) {
            console.error('File upload error:', uploadError);
            setUploadError(uploadError.message || 'Failed to upload file');
          } else if (data?.path) {
            const { data: { publicUrl } } = supabase.storage
              .from('peo-quotes')
              .getPublicUrl(data.path);
              
            quoteFileUrl = publicUrl;
            console.log('File uploaded successfully:', publicUrl);
          }
        } catch (uploadErr: any) {
          console.error('File upload error:', uploadErr);
          setUploadError(uploadErr.message || 'Failed to upload file');
        }
      }

      // Parse amounts to numbers or null if empty
      const monthlyAmount = editForm.monthlyAmount ? parseFloat(editForm.monthlyAmount) : null;
      const annualAmount = editForm.annualAmount ? parseFloat(editForm.annualAmount) : null;
      
      // Create update object
      const updateData = {
        monthly_cost: monthlyAmount,
        annual_cost: annualAmount,
        valid_until: editForm.validUntil || null,
        quote_file_url: quoteFileUrl,
        updated_at: new Date().toISOString()
      };
      
      console.log('Updating quote with data:', {
        quoteId: selectedQuote.id,
        updateData
      });

      // First try to get the current record to compare
      const { data: currentRecord, error: fetchError } = await supabase
        .from('peo_quotes')
        .select('*')
        .eq('id', selectedQuote.id)
        .single();
      
      if (fetchError) {
        console.error('Error fetching current record:', fetchError);
        setUpdateError('Failed to fetch current record: ' + fetchError.message);
      } else {
        console.log('Current record before update:', currentRecord);
      }

      const { error, data } = await supabase
        .from('peo_quotes')
        .update(updateData)
        .eq('id', selectedQuote.id)
        .select();

      if (error) {
        console.error('Database update error:', error);
        setUpdateError(error.message);
        throw error;
      }

      console.log('Quote updated successfully, response data:', data);
      
      // Verify the update was successful by fetching the latest data
      const { data: verifyData, error: verifyError } = await supabase
        .from('peo_quotes')
        .select('*')
        .eq('id', selectedQuote.id)
        .single();
      
      if (verifyError) {
        console.error('Error verifying update:', verifyError);
      } else {
        console.log('Updated record verification:', verifyData);
      }
      
      // Show success toast
      toast({
        title: "Quote updated successfully",
        description: uploadError 
          ? "Quote details updated but file upload failed"
          : "All quote details have been updated.",
      });

      // In case sonner toast is used elsewhere in the app
      sonnerToast.success("Quote details updated successfully");

      // Trigger refresh of quotes list
      onSuccess();
      setIsEditDialogOpen(false);
    } catch (error: any) {
      console.error('Error in handleEditQuote:', error);
      
      // Show error toast
      toast({
        title: "Error updating quote",
        description: `There was an error updating the quote: ${error.message}`,
        variant: "destructive",
      });
      
      sonnerToast.error(`Error updating quote: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    selectedQuote,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isSubmitting,
    uploadError,
    updateError,
    editForm,
    setEditForm,
    openEditDialog,
    handleEditQuote
  };
};
